import { useState } from 'react';
import type { ShiftSummaryDto, TenderExpectationDto } from '@matix/api-client';
import { roundMoney } from '@matix/utils';
import { DENOMINATIONS, useShift } from '../../store/shift';
import { amountStep, amountText, moneyPrecision } from '../../store/currency';
import { fmt } from '../../lib/mock-data';
import { useLang } from '../../lib/language';
import { XIcon } from '../../components/icons';

/**
 * Close-shift popup — the only place cash is entered.
 *
 * The close screen behind it is for reviewing the shift's invoices; the
 * cashier only touches money here, on the way out. "Type total amount" is
 * the default because most drawers are counted once, not sorted into notes.
 *
 * Short/over are shown live: the blind-close pattern this screen used to
 * enforce was dropped because who may see drawer cash is a permission
 * question, not something to hide from every cashier.
 */

interface Props {
  summary: ShiftSummaryDto;
  submitting: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  /** Card / network tenders to count (empty = the section is not shown). */
  tenders?: TenderExpectationDto[];
  tenderTexts?: Record<number, string>;
  onTenderText?: (paymentTypeId: number, text: string) => void;
}

export function CloseShiftDialog({ summary, submitting, onCancel, onConfirm, tenders = [], tenderTexts = {}, onTenderText }: Props) {
  const {
    closeMode, closeCounts, closeSingleAmount, closeNotes, closeTotal,
    setCloseMode, setCloseCount, setCloseSingleAmount, setCloseNotes,
  } = useShift();
  const { t, lang } = useLang();

  // The typed amount is kept as the text the cashier typed. Deriving it from
  // the store's number made a typed "0" vanish (`0 || ''`), which read as the
  // field refusing input on a day with no cash sales — and a zero drawer is a
  // valid close, so both the field and the button must accept it.
  const [singleText, setSingleText] = useState(
    closeSingleAmount > 0 ? String(closeSingleAmount) : '',
  );

  const counted = closeTotal();
  const expected = summary.expected_breakdown.expected_total;
  const diff = round2(counted - expected);
  const short = diff < 0 ? -diff : 0;
  const over = diff > 0 ? diff : 0;

  return (
    <div className="modal-overlay show" onClick={submitting ? undefined : onCancel}>
      <div
        className="modal close-shift-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}
      >
        <div className="modal-header">
          <div className="modal-header-title">
            <div className="modal-header-name">{t({ en: 'Close shift', ar: 'إغلاق الوردية' })}</div>
            <div className="modal-header-name-ar">
              {t({ en: 'Count the cash in the drawer', ar: 'عُدّ النقدية الموجودة في الدرج' })}
            </div>
          </div>
          <button
            type="button"
            className="modal-close"
            onClick={onCancel}
            disabled={submitting}
            aria-label={t({ en: 'Cancel', ar: 'إلغاء' })}
          >
            <XIcon size={18} />
          </button>
        </div>

        <div className="modal-body">
          <div className="shift-mode-toggle">
            <button
              type="button"
              className={closeMode === 'single' ? 'active' : ''}
              onClick={() => setCloseMode('single')}
            >
              {t({ en: 'Type total amount', ar: 'إدخال المبلغ الإجمالي' })}
            </button>
            <button
              type="button"
              className={closeMode === 'denom' ? 'active' : ''}
              onClick={() => setCloseMode('denom')}
            >
              {t({ en: 'Count by denomination', ar: 'العد حسب الفئة' })}
            </button>
          </div>

          <label className="login-label" style={{ marginBottom: 6, display: 'block' }}>
            {t({ en: 'Cash amount', ar: 'المبلغ النقدي' })}
          </label>

          {closeMode === 'single' ? (
            <input
              type="number"
              className="single-amount-input"
              min={0}
              step={amountStep()}
              value={singleText}
              placeholder={amountText(0)}
              onChange={(e) => {
                setSingleText(e.target.value);
                setCloseSingleAmount(parseFloat(e.target.value) || 0);
              }}
              autoFocus
            />
          ) : (
            <>
              <div className="denom-grid">
                {DENOMINATIONS.map((d) => {
                  const key = String(d.value);
                  const count = closeCounts[key] ?? 0;
                  const subtotal = count * d.value;
                  return (
                    <div className="denom-row" key={key}>
                      <div className={`denom-bill ${d.kind === 'coin' ? 'small' : ''}`}>
                        {d.label}
                      </div>
                      <span className="denom-x">×</span>
                      <input
                        type="number"
                        className="denom-input"
                        min={0}
                        step={1}
                        value={count || ''}
                        placeholder="0"
                        onChange={(e) => setCloseCount(key, parseInt(e.target.value) || 0)}
                      />
                      <div className={`denom-subtotal ${subtotal === 0 ? 'muted' : ''}`}>
                        {fmt(subtotal)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="close-dialog-counted">
                <span>{t({ en: 'Counted', ar: 'المعدود' })}</span>
                <b>{fmt(counted)}</b>
              </div>
            </>
          )}

          {tenders.length > 0 && (
            <div className="tender-count" aria-label={t({ en: 'Card and network count', ar: 'عدّ البطاقات والشبكة' })}>
              <div className="tender-count-title">
                {t({ en: 'Card / network count', ar: 'عدّ البطاقات والشبكة' })}
                <span>{t({ en: 'From the terminal settlement slips', ar: 'من إيصالات تسوية الجهاز' })}</span>
              </div>
              {tenders.map((x) => {
                const text = tenderTexts[x.payment_type_id] ?? '';
                const counted = text.trim() === '' ? null : parseFloat(text) || 0;
                const d = counted == null ? null : round2(counted - x.expected);
                return (
                  <div className="tender-count-row" key={x.payment_type_id}>
                    <div className="tc-name">
                      {lang === 'ar' ? x.label_ar || x.label : x.label}
                      <small>
                        {t({ en: 'Expected', ar: 'المتوقع' })} {fmt(x.expected)} · {x.receipt_count}
                      </small>
                    </div>
                    <input
                      type="number"
                      min={0}
                      step={amountStep()}
                      aria-label={t({ en: `Counted ${x.label}`, ar: `المعدود ${x.label_ar || x.label}` })}
                      placeholder={t({ en: 'Counted', ar: 'المعدود' })}
                      value={text}
                      onChange={(e) => onTenderText?.(x.payment_type_id, e.target.value)}
                    />
                    <div className={`tc-diff ${d == null ? 'muted' : d < 0 ? 'bad' : d > 0 ? 'good' : ''}`}>
                      {d == null ? '—' : d === 0 ? t({ en: 'Matches', ar: 'مطابق' }) : `${d > 0 ? '+' : ''}${fmt(d)}`}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          <label className="login-label" style={{ margin: '16px 0 6px', display: 'block' }}>
            {t({ en: 'Notes (optional)', ar: 'ملاحظات (اختياري)' })}
          </label>
          <textarea
            className="shift-notes"
            value={closeNotes}
            onChange={(e) => setCloseNotes(e.target.value)}
            placeholder={t({
              en: "Anything the manager should know about today's variance?",
              ar: 'هل هناك ما ينبغي أن يعرفه المدير عن فرق اليوم؟',
            })}
          />

          <div className="close-dialog-variance">
            <div className="cdv-cell">
              <div className="cdv-label">{t({ en: 'Expected cash', ar: 'النقد المتوقع' })}</div>
              <div className="cdv-value">{fmt(expected)}</div>
            </div>
            <div className={`cdv-cell ${short > 0 ? 'bad' : ''}`}>
              <div className="cdv-label">{t({ en: 'Cash short', ar: 'عجز نقدي' })}</div>
              <div className="cdv-value">{fmt(short)}</div>
            </div>
            <div className={`cdv-cell ${over > 0 ? 'good' : ''}`}>
              <div className="cdv-label">{t({ en: 'Cash over', ar: 'زيادة نقدية' })}</div>
              <div className="cdv-value">{fmt(over)}</div>
            </div>
          </div>
        </div>

        <div className="modal-footer">
          <div />
          <div className="modal-actions">
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={submitting}>
              {t({ en: 'Cancel', ar: 'إلغاء' })}
            </button>
            <button
              type="button"
              className="btn-primary-modal"
              onClick={onConfirm}
              disabled={counted < 0 || submitting}
            >
              {submitting
                ? t({ en: 'Closing…', ar: 'جارٍ الإغلاق…' })
                : t({ en: 'Confirm close', ar: 'تأكيد الإغلاق' })}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function round2(n: number): number {
  // Money rounds at the tenant currency's precision (3 on KWD) — the drawer
  // difference must be countable in the smallest unit the tenant actually has.
  return roundMoney(n, moneyPrecision());
}
